import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import { isEmbedded, markEmbedded, embedPartner, RETAIL_TRUST, ZURICH } from "./utils/embed/embed";
import { startRetailTrustAuth } from "./utils/embed/partnerEmbed";
import { initEmbedChild } from "./utils/embed/zurichEmbed";
import EventBus from "./helpers/eventbus";
import axios from "axios";
import VueAxios from "vue-axios";
import BootstrapVue from "bootstrap-vue";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue/dist/bootstrap-vue.css";
import "fullcalendar/dist/fullcalendar.css";
import config from "./config/constants";
import "animate.css";
import { library } from "@fortawesome/fontawesome-svg-core";

import VueConfetti from "vue-confetti";
Vue.use(VueConfetti);

import {
  faTimesCircle,
  faTimes,
  faClock,
  faUserClock,
  faMapMarkerAlt,
  faUser,
  faEuroSign,
  faChevronLeft,
  faChevronRight,
  faCalendar,
  faBars,
  faEllipsisH,
  faPlay,
  faCheck,
  faLock,
  faEye,
  faEyeSlash,
  faSearch,
  faPills,
  faStethoscope,
  faSyringe,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  faBarsFilter,
  faMessage,
  faWrench,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import "@fortawesome/fontawesome-pro/css/all.css";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import "slick-carousel/slick/slick.css";
import VueDateFns from "vue-date-fns";
import IdleVue from "idle-vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
// import VueGtag from "vue-gtag";
import VuePapaParse from "vue-papa-parse";
import * as Sentry from "@sentry/vue";
import { BrowserTracing } from "@sentry/browser";
import {
  initialiseAnalytics,
  identifyUser,
  initialisePageTracking,
  updatePageKey,
} from "./utils/analytics/analytics";
import {
  initialiseIntercom,
  identifyIntercomUser,
  updateIntercom,
} from "./utils/intercom/intercom";
import { scrubGuestJoinEvent } from "./utils/sentryScrub/sentryScrub";
import { isTimezoneSupportEnabled } from "./utils/timezone/timezone";

Vue.use(VuePapaParse);
initialiseAnalytics();

// CR-300: the guest join route is anonymous by design — never identify a real
// (possibly stale, persisted-from-a-different-login) user to PostHog/Intercom
// there, don't boot Intercom on it at all, and don't register the token-bearing
// URL as the page_key super-property (initialisePageTracking reads
// window.location.pathname before GuestJoin.vue's own URL-hardening runs).
const isGuestJoinRoute = window.location.pathname.startsWith("/join");

if (!isGuestJoinRoute) {
  initialisePageTracking();
  initialiseIntercom();
}

// Re-identify the PostHog and Intercom user on page refresh — vuex-persistedstate rehydrates the
// Vuex store from localStorage, but the analytics and intercom module-level state resets to null
// on every page load. Without this, events fired before the next login would have user_id: null,
// and Intercom would not boot for a user who is already authenticated.
const persistedUser = store.state.user;
if (
  !isGuestJoinRoute &&
  persistedUser.authenticated &&
  persistedUser.user_id &&
  persistedUser.user_client_id
) {
  identifyUser({
    userId: String(persistedUser.user.user_id),
    clientId: String(persistedUser.user_client_id),
    userRole: persistedUser.user_role || "",
    lang: (store.state as any).lang || "",
  });
  identifyIntercomUser({
    userId: String(persistedUser.user.user_id),
    clientId: String(persistedUser.user_client_id),
    userRole: persistedUser.user_role || "",
    lang: (store.state as any).lang || "",
    skin: (store.state as any).client?.skin,
    userHash: (persistedUser.user as any).intercom_user_hash,
  });
}

// Page key tracking — updates page_key / last_page_key super properties on every SPA navigation.
// Uses route names converted from camelCase to Title Case (e.g. "digitalGym" → "Digital Gym").
// Intercom('update') is fired alongside so banner / in-app message targeting can re-evaluate
// against the current route.
router.afterEach((to, from) => {
  if (!from.name) return; // skip initial navigation (handled by initialisePageTracking)
  updatePageKey(to.name as string);
  updateIntercom();
});

Sentry.init({
  Vue,
  dsn: "https://077f159660354a2894daf246ce39de83@sentry.spectrum.life/12",
  release: process.env.VUE_APP_VERSION,
  environment: process.env.NODE_ENV,
  beforeSend: scrubGuestJoinEvent,
  // Empty = attach sentry-trace/baggage headers to nothing. On production,
  // injecting them caused CORS preflight failures against api.spectrum.life
  // (which doesn't allow those headers), silently blocking all API requests
  // including login. This worked under @sentry/tracing v6's `tracingOrigins`,
  // a client-side-only filter; v7 replaced it with `tracePropagationTargets`,
  // which actively injects the headers into matching outbound fetch/XHR.
  //
  // Set here rather than on BrowserTracing: when both are present the SDK
  // uses the top-level one and warns about the duplicate (browsertracing.js
  // resolves `clientOptions.tracePropagationTargets || this.options...`).
  //
  // Re-enable once the API's CORS config allows sentry-trace and baggage.
  tracePropagationTargets: [],
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
    }),
  ],
  tracesSampleRate: 1.0,
});

// Vue.use(VueGtag, {
//   config: { id: process.env.VUE_APP_GA_TRACKING_ID },
//   appName: 'UP-SPA',
// }, router);

Vue.component("v-select", vSelect);

const eventsHub = new Vue();
const options = { eventEmitter: eventsHub, idleTime: 7200000 };

Vue.use(IdleVue, options);

Vue.use(VueDateFns);
// @ts-ignore — mixing FA v5 free and v6 pro icon types causes a false type mismatch
library.add(
  faTimesCircle,
  faTimes,
  faClock,
  faUserClock,
  faMapMarkerAlt,
  faUser,
  faEuroSign,
  faChevronLeft,
  faChevronRight,
  faCalendar,
  faBars,
  faEllipsisH,
  faPlay,
  faCheck,
  faLock,
  faEye,
  faEyeSlash,
  faSearch,
  // @ts-ignore
  faBarsFilter,
  faMessage,
  faPills,
  faWrench,
  faStethoscope,
  faSyringe,
  faArrowRight,
  faXmark,
);
Vue.component("font-awesome-icon", FontAwesomeIcon);
Vue.use(BootstrapVue);

axios.defaults.headers.common["Content-type"] =
  "application/x-www-form-urlencoded; charset=utf-8";
if (isTimezoneSupportEnabled()) {
  axios.defaults.headers.common["X-Response-Timezone"] = "UTC";
}

Vue.use(VueAxios, axios);
// @ts-ignore — config is used as a Vue plugin but not typed as one
Vue.use(config);

Vue.config.productionTip = false;

import "./mixins/generalMixin";

// Set the embed class before mount so hidden chrome (top nav, footer) never flashes.
const embedded = isEmbedded();
if (embedded) markEmbedded();

new Vue({
  router,
  store,
  // @ts-ignore — axios is attached to the Vue instance via vue-axios but not in the type definition
  axios,
  render: (h) => h(App),
}).$mount("#app");

// Retail Trust embed: run the OAuth PKCE (S256) handshake — send the code_challenge
// to the parent, receive the code, exchange it at /api/oauth/token, then set the
// Bearer + establish the session.
//
// Gated on the partner, not on `embedded` alone. This handshake only has a
// counterparty on Retail Trust's pages; starting it inside any other partner's
// iframe just holds the loader for 40s and then shows an auth error, because
// nobody is listening for sl:ready. Other embeds fall through to the app's normal
// behaviour, which means the member gets the ordinary sign-in screen in the frame.
if (embedPartner() === RETAIL_TRUST) {
  let stopListening: (() => void) | null = null;

  // If the handshake can't establish a session, say so instead of closing the iframe
  // out from under the member with no explanation — EmbedAuthStatus.vue shows the
  // reason and a Retry button that re-runs this from scratch.
  const reportAuthFailure = (reason: string): void => {
    store.commit("setEmbedAuthPending", false);
    store.commit("setEmbedAuthError", reason);
  };

  const runHandshake = (): void => {
    stopListening?.();
    store.commit("setEmbedAuthError", null);
    // Held until the handshake resolves so the app shows a loader instead of briefly
    // bouncing the member to our sign-in screen (which also discarded the deep link).
    store.commit("setEmbedAuthPending", true);

    // Don't hold the loader forever if the partner never answers sl:ready.
    const handshakeTimeout = window.setTimeout(() => {
      if (store.state.embedAuthPending) reportAuthFailure("handshake_timeout");
    }, 40000);

    stopListening = startRetailTrustAuth({
      onToken: async (accessToken) => {
        window.clearTimeout(handshakeTimeout);
        const authenticated = await store.dispatch("retailTrustSignIn", {
          accessToken,
          router,
        });
        store.commit("setEmbedAuthPending", false);
        if (!authenticated) reportAuthFailure("auth_failed");
      },
      onError: () => {
        window.clearTimeout(handshakeTimeout);
        reportAuthFailure("auth_failed");
      },
    });
  };

  EventBus.$on("retryEmbedAuth", runHandshake);
  runHandshake();
} else if (embedPartner() === ZURICH) {
  // Zurich embed: drive the child side of the postMessage handshake — publish a
  // PKCE challenge, redeem the one-time code on parent:init, sign in with the
  // resulting token, report height, handle logout.
  //
  // Holds the same loader as Retail Trust, but a failure or timeout releases it
  // to the ordinary sign-in screen rather than an error: a plain embed with no
  // partner session must still be able to sign in.
  //
  // Async only because the challenge has to exist before readiness is announced.
  // Nothing can be done about a failure this early — there is no session and no
  // agreed contract yet — so the rejection is swallowed rather than left floating.
  initEmbedChild(store, router).catch(() => {});
}
